import { Chip, Stack, Theme, Typography, useTheme } from '@mui/joy';
import { Circle } from '@phosphor-icons/react';
import { t } from 'i18next';

import { DEFAULT_PLACEHOLDER } from 'core/constants';
import { ExternalOrderStatusType } from 'quotes/enums';
import { Color, Level, ModeType, Size, Variant } from 'theme/enums';

interface IProps {
  value: ExternalOrderStatusType;
  size?: Size;
  displayAsChip?: boolean;
}

export const ExternalOrderStatus = ({ value, size = Size.SM, displayAsChip = true }: IProps) => {
  const theme = useTheme();

  if (!value) {
    return <>{DEFAULT_PLACEHOLDER}</>;
  }

  if (!displayAsChip) {
    return (
      <Typography
        level={Level.TITLE_SM}
        startDecorator={<Circle weight="fill" style={{ fontSize: 10 }} />}
        sx={{
          color: getColor(value, theme),
          fontWeight: 600,
          textWrap: 'balance',
        }}
      >
        {getExternalOrderStatusLabel(value)}
      </Typography>
    );
  }

  return (
    <Stack sx={{ padding: '4px 0', gap: 0.5 }}>
      <Chip
        variant={Variant.SOFT}
        size={size}
        sx={[
          {
            color: getColor(value, theme),
            backgroundColor: getBackgroundColor(value, theme),
            textWrap: 'balance',
            fontWeight: 600,
          },
          getDarkChipSx(value),
        ]}
      >
        {getExternalOrderStatusLabel(value)}
      </Chip>
    </Stack>
  );
};

const STATUS_PALETTES: Partial<Record<ExternalOrderStatusType, Color>> = {
  [ExternalOrderStatusType.CANCELLED]: Color.DANGER,
  [ExternalOrderStatusType.CLOSED]: Color.SUCCESS,
  [ExternalOrderStatusType.DRAFT]: Color.WARNING,
  [ExternalOrderStatusType.OPEN]: Color.PRIMARY,
};

// The dark scale is inverted (theme.tsx): steps 500 on 200 turn into dark text on a dark tint
// there, so the chip takes light text on a mid tint instead.
const getDarkChipSx = (value: ExternalOrderStatusType) => (theme: Theme) => {
  const palette = STATUS_PALETTES[value];

  return palette
    ? {
        [theme.getColorSchemeSelector(ModeType.DARK)]: {
          color: `${palette}.800`,
          backgroundColor: `${palette}.300`,
        },
      }
    : {};
};

const getColor = (value: ExternalOrderStatusType, theme: Theme) => {
  switch (value) {
    case ExternalOrderStatusType.DRAFT:
      return theme.palette.warning[500];
    case ExternalOrderStatusType.OPEN:
      return theme.palette.primary[500];
    case ExternalOrderStatusType.SUBMITTED:
      return theme.palette.custom.brown.main;
    case ExternalOrderStatusType.CANCELLED:
      return theme.palette.danger[500];
    case ExternalOrderStatusType.CLOSED:
      return theme.palette.success[500];
    default:
      return theme.palette.custom.purple;
  }
};

const getBackgroundColor = (value: ExternalOrderStatusType, theme: Theme) => {
  switch (value) {
    case ExternalOrderStatusType.DRAFT:
      return theme.palette.warning[200];
    case ExternalOrderStatusType.OPEN:
      return theme.palette.primary[200];
    case ExternalOrderStatusType.SUBMITTED:
      return theme.palette.custom.brown.light;
    case ExternalOrderStatusType.CANCELLED:
      return theme.palette.danger[200];
    case ExternalOrderStatusType.CLOSED:
      return theme.palette.success[200];
    default:
      return theme.palette.custom.cloud;
  }
};

export const getExternalOrderStatusLabel = (value: ExternalOrderStatusType) => {
  switch (value) {
    case ExternalOrderStatusType.DRAFT:
      return t('labelExternalOrderStatusDraft');
    case ExternalOrderStatusType.OPEN:
      return t('labelExternalOrderStatusOpen');
    case ExternalOrderStatusType.SUBMITTED:
      return t('labelExternalOrderStatusSubmitted');
    case ExternalOrderStatusType.CANCELLED:
      return t('labelExternalOrderStatusCancelled');
    case ExternalOrderStatusType.CLOSED:
      return t('labelExternalOrderStatusClosed');
    default:
      return value;
  }
};
